// Client-side translations
// The language is determined from the data-lang attribute on the body element

export type Lang = "en" | "es";

export interface ClientTranslations {
  // Debug/Research toggles
  debug: string;
  debugActive: string;
  showResearch: string;
  showResearchActive: string;

  // Audience summary
  audiencePrompt: string;
  audienceSummaryClose: string;
  audienceSummaryExtended: string;
  audienceSummarySympathetic: string;
  audienceSummarySomeDisagree: string;
  audienceSummaryGeneral: string;
  audienceSummaryNeighbors: string;
  audienceSummaryFaith: string;

  // Writing stages (labels used for summary text)
  stages: {
    "not-started": string;
    stuck: string;
    "draft-review": string;
  };

  // Item actions
  markAsDone: string;
  markAsUnread: string;
  movedToReadItems: string;
  readItems: string;
  hideReadItems: string;
  readBadge: string;
  star: string;
  starred: string;

  // Guidance sections
  strategiesToTry: string;
  examplesLabel: string;
  researchSources: string;

  // Counts
  ideas: string;
  strategies: string;
  techniques: string;

  // Topic header
  writingAbout: string;

  // Intro text
  youSaid: string;
  heresGuidance: string;
  basedOnSelections: string;

  // Saved page
  savedHeaderLink: string;
  savedEmptyState: string;
  savedBackToGuide: string;
  copyWorksheet: string;
  copyWorksheetDescription: string;
  copied: string;
}

const translations: Record<Lang, ClientTranslations> = {
  en: {
    debug: "Debug",
    debugActive: "Debug ✓",
    showResearch: "Show research",
    showResearchActive: "Show research ✓",

    audiencePrompt: "Who are you writing to?",
    audienceSummaryClose: "Close friends and family",
    audienceSummaryExtended: "Extended network",
    audienceSummarySympathetic: "mostly sympathetic",
    audienceSummarySomeDisagree: "some may disagree",
    audienceSummaryGeneral: "general audience",
    audienceSummaryNeighbors: "neighbors",
    audienceSummaryFaith: "faith community",

    stages: {
      "not-started": "I haven't started writing yet",
      stuck: "I've started but I'm stuck",
      "draft-review": "I have something written",
    },

    markAsDone: "Mark as done",
    markAsUnread: "Mark as unread",
    movedToReadItems: "Moved to read items",
    readItems: "Read items",
    hideReadItems: "Hide read items",
    readBadge: "(read)",
    star: "☆ Save",
    starred: "★ Saved",

    strategiesToTry: "Strategies to try:",
    examplesLabel: "Examples:",
    researchSources: "Research Sources",

    ideas: "ideas",
    strategies: "strategies",
    techniques: "techniques",

    writingAbout: "Writing about:",

    youSaid: "You said:",
    heresGuidance: "- here's guidance to help.",
    basedOnSelections: "Based on your selections, here's guidance to help you write.",

    savedHeaderLink: "★ Saved",
    savedEmptyState: "You haven't saved any items yet. Browse the guide and click \"Save\" on items you want to come back to.",
    savedBackToGuide: "Go to the guide",
    copyWorksheet: "Copy as worksheet",
    copyWorksheetDescription: "copies all your saved items as text you can paste into a document. Each prompt line starts with > so you can delete them as you write.",
    copied: "Copied!",
  },

  es: {
    debug: "Depurar",
    debugActive: "Depurar ✓",
    showResearch: "Mostrar investigación",
    showResearchActive: "Mostrar investigación ✓",

    audiencePrompt: "¿Para quién escribes?",
    audienceSummaryClose: "Amigos cercanos y familia",
    audienceSummaryExtended: "Red extendida",
    audienceSummarySympathetic: "mayormente comprensivos",
    audienceSummarySomeDisagree: "algunos pueden discrepar",
    audienceSummaryGeneral: "audiencia general",
    audienceSummaryNeighbors: "vecinos",
    audienceSummaryFaith: "comunidad de fe",

    stages: {
      "not-started": "Todavía no he empezado a escribir",
      stuck: "He empezado pero estoy atascado/a",
      "draft-review": "Tengo algo escrito",
    },

    markAsDone: "Marcar como leído",
    markAsUnread: "Marcar como no leído",
    movedToReadItems: "Movido a elementos leídos",
    readItems: "Elementos leídos",
    hideReadItems: "Ocultar elementos leídos",
    readBadge: "(leído)",
    star: "☆ Guardar",
    starred: "★ Guardado",

    strategiesToTry: "Estrategias a probar:",
    examplesLabel: "Ejemplos:",
    researchSources: "Fuentes de investigación",

    ideas: "ideas",
    strategies: "estrategias",
    techniques: "técnicas",

    writingAbout: "Tema:",

    youSaid: "Dijiste:",
    heresGuidance: "- aquí tienes guía para ayudarte.",
    basedOnSelections: "Basado en tus selecciones, aquí tienes guía para ayudarte a escribir.",

    savedHeaderLink: "★ Guardados",
    savedEmptyState: "No has guardado ningún elemento todavía. Explora la guía y haz clic en \"Guardar\" en los elementos que quieras volver a ver.",
    savedBackToGuide: "Ir a la guía",
    copyWorksheet: "Copiar como hoja de trabajo",
    copyWorksheetDescription: "copia todos tus elementos guardados como texto que puedes pegar en un documento. Cada línea de indicación comienza con > para que puedas eliminarlas mientras escribes.",
    copied: "¡Copiado!",
  },
};

// Get current language from body data-lang attribute
export function getLang(): Lang {
  const lang = document.body.dataset.lang as Lang | undefined;
  return lang === "es" ? "es" : "en";
}

// Get translations for current language
export function getTranslations(): ClientTranslations {
  return translations[getLang()];
}

// Shorthand
export const tr = () => getTranslations();
